import React, { createContext, useContext, useMemo } from 'react';
import type { ViewProps } from 'react-native';

import { useCustomer } from '@order/Customer';
import { useCart, useCustomerCredit } from '@order/hooks';

import {
  useAvailableWantedTimes,
  useCheckoutCredit,
  useCheckoutFieldsValidation,
  useCheckoutHandlers,
  useCheckoutPayload,
  useDeliveryEstimate,
  useFutureDateModal,
  useOrderAddress,
  useOrderValidation,
  useSubmitOrder,
} from './hooks';

// ─── Context ──────────────────────────────────────────────────────

const CheckoutContext = createContext<CheckoutCtx | undefined>(undefined);

export const CheckoutContextProvider = ({ children }: ViewProps) => {
  // ─── Checkout Queries / Mutations ───────────────────────────────

  const customerData = useCustomer();
  const cartData = useCart();
  const wantedTimesData = useAvailableWantedTimes();
  const creditData = useCustomerCredit();
  const submitOrderData = useSubmitOrder();

  // ─── Query Derived Data ─────────────────────────────────────────

  const deliveryAddressId = cartData.cart?.deliveryOrderDetail?.address?.id;
  const deliveryEstimateData = useDeliveryEstimate(deliveryAddressId);
  const addressData = useOrderAddress(cartData.cart, customerData.customer);

  // ─── Checkout State ─────────────────────────────────────────────

  const payloadState = useCheckoutPayload();
  const futureDateModalState = useFutureDateModal();
  const validationState = useCheckoutFieldsValidation(cartData.cartOrderType);
  const payloadHandlers = useCheckoutHandlers({
    setPayload: payloadState.setPayload,
    setShouldShowFutureDateModal:
      futureDateModalState.setShouldShowFutureDateModal,
  });

  // ─── State Derived Data ─────────────────────────────────────────

  const checkoutCredit = useCheckoutCredit({
    ...cartData,
    ...creditData,
    ...payloadState,
  });

  const orderValidation = useOrderValidation({
    ...cartData,
    ...payloadState.payload,
  });

  // ─── Memoized Data ──────────────────────────────────────────────

  const value: CheckoutCtx = useMemo(
    () => ({
      ...customerData,
      ...cartData,
      ...wantedTimesData,
      ...creditData,
      ...submitOrderData,
      ...deliveryEstimateData,
      ...addressData,
      ...payloadState,
      ...futureDateModalState,
      ...validationState,
      ...payloadHandlers,
      ...checkoutCredit,
      ...orderValidation,
    }),
    [
      customerData,
      cartData,
      wantedTimesData,
      creditData,
      submitOrderData,
      deliveryEstimateData,
      addressData,
      payloadState,
      futureDateModalState,
      validationState,
      payloadHandlers,
      checkoutCredit,
      orderValidation,
    ],
  );

  // ─── Context Provider ───────────────────────────────────────────

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

// ─── Hooks ────────────────────────────────────────────────────────

export const useCheckoutContext = () => {
  const context = useContext(CheckoutContext);

  if (context === undefined) {
    throw new Error(
      'useCheckoutContext must be used within a <CheckoutContextProvider>',
    );
  }

  return context;
};

// ─── Types ────────────────────────────────────────────────────────

type CheckoutCtx = ReturnType<typeof useCheckoutPayload> &
  ReturnType<typeof useFutureDateModal> &
  ReturnType<typeof useCheckoutFieldsValidation> &
  ReturnType<typeof useCheckoutHandlers> &
  ReturnType<typeof useCustomer> &
  ReturnType<typeof useCart> &
  ReturnType<typeof useDeliveryEstimate> &
  ReturnType<typeof useOrderAddress> &
  ReturnType<typeof useAvailableWantedTimes> &
  ReturnType<typeof useCustomerCredit> &
  ReturnType<typeof useSubmitOrder> &
  ReturnType<typeof useCheckoutCredit> &
  ReturnType<typeof useOrderValidation>;
